import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Loader2, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

interface FAQCategory {
  id: string;
  name: string;
  display_order: number;
  questions: FAQQuestion[];
}

const FAQ = () => {
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [allQuestions, setAllQuestions] = useState<{ question: string; answer: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t, isHebrew } = useStaticTranslation("pages");
  const { t: seoT } = useStaticTranslation("seo");

  useEffect(() => {
    loadFAQData();
  }, []);

  const loadFAQData = async () => {
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("faq_categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (categoriesError) throw categoriesError;

      const { data: questionsData, error: questionsError } = await supabase
        .from("faq_questions")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (questionsError) throw questionsError;

      const categoriesWithQuestions = (categoriesData || []).map((category) => ({
        ...category,
        questions: (questionsData || []).filter(
          (q) => q.category_id === category.id
        ),
      }));

      setCategories(categoriesWithQuestions);
      
      const allFAQs = (questionsData || []).map((q) => ({
        question: q.question,
        answer: q.answer,
      }));
      setAllQuestions(allFAQs);
    } catch (error) {
      console.error("Error loading FAQ data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title={seoT("faqTitle", "שאלות נפוצות")}
        description={seoT("faqDescription", "שאלות נפוצות על אליס תכשיטים - תשובות לשאלות על רכישה, משלוחים, החזרות, אחריות ועוד. מצאו את כל התשובות במקום אחד")}
        canonical="https://alice.jewelry/faq"
        breadcrumbs={[
          { name: seoT("home", "דף הבית"), url: '/' },
          { name: seoT("breadcrumbFaq", "שאלות נפוצות"), url: '/faq' },
        ]}
        faqItems={allQuestions.length > 0 ? allQuestions : undefined}
      />
      <div className="min-h-screen flex flex-col" dir={isHebrew ? "rtl" : "ltr"}>
        <Header />

        <main className="flex-1 pt-32 pb-16 px-4 md:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-primary/10 rounded-full">
                  <HelpCircle className="h-10 w-10 text-primary" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4">
                {t("faqTitle", "שאלות נפוצות")}
              </h1>
              <p className="text-muted-foreground text-lg">
                {t("faqSubtitle", "מצאו תשובות לשאלות הנפוצות ביותר")}
              </p>
            </div>

            {isLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {!isLoading && categories.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {t("noFaqQuestions", "אין שאלות נפוצות להצגה כרגע")}
              </div>
            )}

            {!isLoading && categories.length > 0 && (
              <div className="space-y-8">
                {categories.map((category) => (
                  <div key={category.id} className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-xl font-medium text-foreground mb-4 pb-3 border-b border-border">
                      {category.name}
                    </h2>
                    
                    {category.questions.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        {t("noQuestionsInCategory", "אין שאלות בקטגוריה זו")}
                      </p>
                    ) : (
                      <Accordion type="single" collapsible className="w-full">
                        {category.questions.map((question) => (
                          <AccordionItem
                            key={question.id}
                            value={question.id}
                            className="border-b border-border/50 last:border-b-0"
                          >
                            <AccordionTrigger className={`${isHebrew ? 'text-right' : 'text-left'} hover:no-underline py-4`}>
                              <span className="text-foreground font-normal">
                                {question.question}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground pb-4 whitespace-pre-line leading-relaxed">
                              {question.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-12 text-center p-8 bg-muted/30 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t("faqNotFound", "לא מצאתם תשובה לשאלה שלכם?")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("faqContactUs", "צרו איתנו קשר ונשמח לעזור")}
              </p>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                {t("faqContactButton", "צור קשר")}
              </a>
            </div>
          </div>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default FAQ;
